import { keyframes } from "styled-components";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getMarketplaceProfile } from "../../../Redux/ApiCalls/Dashboard/MarketplaceAPI";
import { MARKETPLACE_GRADIENT } from "../../organisms/Dashboard/Marketplace/marketplaceConstants";

export const gradient = MARKETPLACE_GRADIENT;

export const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const isProfileCompleted = (prof?: any): boolean => {
  const isSkipped = localStorage.getItem("marketplace_profile_skipped") === "true";
  const isExplicitlyCreated = localStorage.getItem("marketplace_profile_created") === "true";

  if (isExplicitlyCreated) {
    return true;
  }
  if (isSkipped) {
    return false;
  }

  let profileData = prof;
  if (!profileData) {
    const cached = localStorage.getItem("marketplace_profile");
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        profileData = parsed?.data || parsed;
      } catch {}
    }
  }

  if (!profileData) return false;

  // A real completed marketplace profile must have at least one substantive seller field
  // (Location, Bio, Profession, Work Categories, Services, Portfolio, Skills, or Business Info)
  const hasLocation = Boolean((profileData.city && profileData.city.trim().length > 0) || (profileData.state && profileData.state.trim().length > 0));
  const hasBio = Boolean(profileData.bio && profileData.bio.trim().length > 0);
  const hasProfession = Boolean(profileData.profession && profileData.profession.trim().length > 0);
  const hasCategories = (Array.isArray(profileData.work_categories) && profileData.work_categories.length > 0) ||
                        (typeof profileData.work_categories === "string" && profileData.work_categories.trim().length > 0);
  const hasServices = (Array.isArray(profileData.services_offered) && profileData.services_offered.length > 0) ||
                      (typeof profileData.services_offered === "string" && profileData.services_offered.trim().length > 0);
  const hasPortfolio = Array.isArray(profileData.portfolio) && profileData.portfolio.length > 0;
  const hasBusinessInfo = Boolean(profileData.business_info && profileData.business_info.trim().length > 0);
  const hasSkills = (Array.isArray(profileData.skills) && profileData.skills.length > 0) ||
                    (typeof profileData.skills === "string" && profileData.skills.trim().length > 0);

  if (hasLocation || hasBio || hasProfession || hasCategories || hasServices || hasPortfolio || hasBusinessInfo || hasSkills) {
    return true;
  }

  return Boolean((profileData.id || profileData._id) && profileData.display_name && !isSkipped);
};

export const isMarketplaceHost = (hostname?: string): boolean => {
  if (typeof window === "undefined") return false;
  const host = (hostname || window.location.hostname || "").toLowerCase().trim();
  const href = (window.location.href || "").toLowerCase();
  return (
    host.includes("marketplace.kwikmemories.com") ||
    host.startsWith("marketplace.") ||
    href.includes("marketplace.kwikmemories.com")
  );
};

export const isMarketplaceDomain = typeof window !== "undefined" && isMarketplaceHost();

export const getMarketplacePath = (routePath: string): string => {
  if (isMarketplaceDomain) {
    let cleaned = routePath.replace(/^\/marketplace/, "");
    if (!cleaned || cleaned.startsWith("?")) {
      cleaned = "/" + cleaned;
    }
    return cleaned;
  }
  return routePath;
};

export const useMarketplaceAuth = (fromPage: string) => {
  const navigate = useNavigate();
  const [authChecking, setAuthChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      navigate(getMarketplacePath("/marketplace"));
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("open-marketplace-login"));
      }, 100);
      return;
    }
    const cached = localStorage.getItem("marketplace_profile");
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        const prof = parsed?.data || parsed;
        if (isProfileCompleted(prof)) {
          setAuthChecking(false);
        }
      } catch {}
    }
    getMarketplaceProfile()
      .then(res => {
        const profileData = res?.data || null;
        if (!isProfileCompleted(profileData)) {
          navigate(getMarketplacePath("/marketplace/profile/create"));
        } else {
          localStorage.setItem("marketplace_profile", JSON.stringify(profileData));
          setAuthChecking(false);
        }
      })
      .catch(() => {
        const stillCached = localStorage.getItem("marketplace_profile");
        if (stillCached) {
          try {
            const parsed = JSON.parse(stillCached);
            const prof = parsed?.data || parsed;
            if (isProfileCompleted(prof)) {
              setAuthChecking(false);
              return;
            }
          } catch {}
        }
        navigate(getMarketplacePath("/marketplace/profile/create"));
      });
  }, [navigate, fromPage]);

  return { authChecking };
};

export const formatINR = (value: string | number): string => {
  return `₹${Number(value).toLocaleString("en-IN")}`;
};

export const formatDate = (dateStr?: string) => {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  } catch {
    return dateStr;
  }
};

export const getErrorMessage = (err: any, fallback = "Something went wrong"): string => {
  return err?.response?.data?.message || err?.response?.data?.detail || err?.message || fallback;
};

export const isLoggedIn = (): boolean => {
  return !!localStorage.getItem("access_token");
};

export const checkProfileCompleteness = (): boolean => {
  const cached = localStorage.getItem("marketplace_profile");
  if (!cached) return false;
  try {
    const parsed = JSON.parse(cached);
    const prof = parsed?.data || parsed;
    return isProfileCompleted(prof);
  } catch {
    return false;
  }
};

export const isAccountFrozen = (): boolean => {
  const cached = localStorage.getItem("marketplace_profile");
  if (!cached) return false;
  try {
    const parsed = JSON.parse(cached);
    const prof = parsed?.data || parsed;
    return prof?.is_active === false;
  } catch {
    return false;
  }
};

export const parseMetadataImageUrls = (metadata: any): string[] => {
  if (!metadata || !metadata.image_urls) return [];
  const urls = metadata.image_urls;
  if (Array.isArray(urls)) return urls;
  if (typeof urls === "string") {
    try {
      const parsed = JSON.parse(urls);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      if (urls.includes(",")) {
        return urls.split(",").map(u => u.trim());
      }
      return [urls];
    }
  }
  return [];
};
